import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import type { MonitoringAiSettings } from '../../constants/monitoringAiSettings';
import type { RiskLandThresholds } from '../../constants/riskLandThresholds';
import {
  unifyFacilityThresholdsToSystem,
  type ThresholdUnifyStats,
} from '../../services/thresholdUnifyService';

type Props = {
  riskThresholds: RiskLandThresholds;
  monitoringAi: MonitoringAiSettings;
  poolCount?: number;
  onDone?: (stats: ThresholdUnifyStats) => void;
};

function formatStatsSummary(stats: ThresholdUnifyStats): string {
  const parts: string[] = [];
  if (stats.pools_updated > 0) {
    parts.push(`시설 ${stats.pools_updated}/${stats.pools_scanned}곳`);
  }
  if (stats.cctvs_updated > 0) {
    parts.push(`CCTV ${stats.cctvs_updated}/${stats.cctvs_scanned}대`);
  }
  return parts.length ? parts.join(' · ') : '변경할 오버라이드 없음';
}

const ThresholdUnifyCard: React.FC<Props> = ({
  riskThresholds,
  monitoringAi,
  poolCount = 0,
  onDone,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastStats, setLastStats] = useState<ThresholdUnifyStats | null>(null);

  const systemSummary = useMemo(
    () =>
      [
        `배회 ${riskThresholds.loitering_stillness_sec}초`,
        `특별구역 ${riskThresholds.special_zone_dwell_sec}초`,
        `밀집 ${riskThresholds.crowd_density_count}명`,
        `검출 conf≥${monitoringAi.min_confidence}`,
        `연속 ${monitoringAi.min_track_hits}회`,
      ].join(' · '),
    [riskThresholds, monitoringAi]
  );

  const runUnify = async () => {
    setConfirmOpen(false);
    setBusy(true);
    setError(null);
    try {
      const res = await unifyFacilityThresholdsToSystem();
      setLastStats(res.stats);
      onDone?.(res.stats);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (e as Error)?.message ||
        '일괄 통일에 실패';
      setError(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.light' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            시설·CCTV 임계 일괄 통일
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            시설·CCTV에 따로 저장된 임계 오버라이드를 해제하고, 아래 이 화면의{' '}
            <strong>현재 시스템 임계</strong>를 승계하도록 맞춤. (익사 motion
            민감도·정지는 제외)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            대상: 시설 육상 임계 · 검출 신뢰도/연속 프레임 · CCTV 특별관리구역 체류(초)
            {poolCount > 0 ? ` · 등록 시설 ${poolCount}곳 전체` : ''}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 2, color: 'primary.main' }}>
            기준(현재 시스템): {systemSummary}
          </Typography>
          {error ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          ) : null}
          {lastStats ? (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              {formatStatsSummary(lastStats)}
              {lastStats.pools_updated === 0 && lastStats.cctvs_updated === 0
                ? ' — 이미 시스템 승계 상태'
                : ' — 시스템 임계로 통일함'}
            </Alert>
          ) : null}
          <Button
            variant="contained"
            color="primary"
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            disabled={busy}
            onClick={() => setConfirmOpen(true)}
          >
            시스템 임계로 일괄 통일
          </Button>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle>시설·CCTV 임계를 시스템 값으로 통일 확인</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" paragraph>
              모든 시설·CCTV에서 아래 항목의 <strong>개별 설정을 지움</strong>.
              이후에는 이 탭에 저장된 시스템 임계가 자동으로 적용됨.
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
              <li>시설 육상·시나리오 임계 (risk_land_thresholds)</li>
              <li>시설·CCTV 검출 신뢰도 하한 / 연속 프레임</li>
              <li>CCTV 특별관리구역 체류(초)</li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2 }}>
              현재 시스템 기준: {systemSummary}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              되돌리려면 시설·CCTV 화면에서 다시 개별 값을 입력해야.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            취소
          </Button>
          <Button variant="contained" onClick={() => void runUnify()} disabled={busy}>
            통일 실행
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThresholdUnifyCard;
